/**
 * UpdatePrompt — in-app update banner / blocking modal.
 *
 * Runs on every authenticated app load. Reads the running build's versionCode
 * (Capacitor native: App.getInfo().build; web/PWA: build-time env var),
 * calls GET /app/version-info, and shows:
 *
 *   • Dismissible banner  — running < latest (soft nudge, per-session dismiss)
 *   • Blocking modal      — running < min_supported OR force_update=true
 *                           (cannot dismiss; must go to Play Store)
 *
 * Renders nothing on web when version codes match.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, RefreshCw, ArrowUpCircle } from 'lucide-react';
import { apiGet } from '../lib/api';

// Build-time constant — set REACT_APP_BUILD_VERSION_CODE in .env.production.
// Must be an integer matching android/app/build.gradle `versionCode`.
const WEB_VERSION_CODE = parseInt(process.env.REACT_APP_BUILD_VERSION_CODE || '1', 10);

const DISMISS_KEY = 'ellora_update_dismissed_vc';  // stores the last dismissed version_code

// iOS detection (iPhone, iPod, and iPad OS which reports MacIntel + touch)
const isIOS = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

async function getRunningVersionCode() {
  try {
    const { Capacitor } = await import('@capacitor/core');
    if (Capacitor.isNativePlatform && Capacitor.isNativePlatform()) {
      const { App } = await import('@capacitor/app');
      const info = await App.getInfo();
      // `build` is the Android versionCode (integer string)
      return parseInt(info?.build || '1', 10);
    }
  } catch { /* not a native build */ }
  return WEB_VERSION_CODE;
}

export default function UpdatePrompt() {
  const [state, setState] = useState(null);   // null | 'banner' | 'blocking'
  const [info, setInfo] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    // Only show update prompts on the native Android Capacitor app.
    // iOS Safari (PWA or browser) cannot visit the Play Store, and the browser
    // handles web updates automatically — showing this banner is wrong and disruptive.
    // Android browser (non-installed PWA) also skips — no Play Store route.
    try {
      const { Capacitor } = await import('@capacitor/core');
      if (!Capacitor.isNativePlatform()) return;  // web/PWA on any platform: never show
    } catch {
      return;  // Capacitor unavailable = definitely web = skip
    }
    if (isIOS()) return;  // iOS regardless of Capacitor state

    try {
      const [runningCode, versionInfo] = await Promise.all([
        getRunningVersionCode(),
        apiGet('/app/version-info'),
      ]);
      if (!versionInfo) return;
      setInfo(versionInfo);
      const latest = parseInt(versionInfo.latest_version_code || 1, 10);
      const minSupported = parseInt(versionInfo.min_supported_code || 1, 10);
      const forceUpdate = !!versionInfo.force_update;

      // Blocking: force_update OR running below minimum
      if (forceUpdate || runningCode < minSupported) {
        setState('blocking');
        return;
      }
      // Banner: running below latest (soft)
      if (runningCode < latest) {
        const lastDismissedVc = parseInt(sessionStorage.getItem(DISMISS_KEY) || '0', 10);
        // Don't show banner again if user already dismissed this version this session
        if (lastDismissedVc < latest) {
          setState('banner');
        }
      }
    } catch { /* version check is never critical — fail silently */ }
  }, []);

  useEffect(() => { check(); }, [check]);

  const openStore = () => {
    const url = info?.play_store_url || 'https://play.google.com/store/apps/details?id=com.elrindia.erp';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const dismissBanner = () => {
    const latest = parseInt(info?.latest_version_code || 1, 10);
    sessionStorage.setItem(DISMISS_KEY, String(latest));
    setDismissed(true);
  };

  if (!state || dismissed) return null;

  // ── Blocking modal ────────────────────────────────────────────────────────
  if (state === 'blocking') {
    return (
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        data-testid="update-blocking-modal"
      >
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="w-14 h-14 rounded-full bg-[#1F7A6E]/10 flex items-center justify-center mx-auto mb-4">
            <ArrowUpCircle size={28} className="text-[#1F7A6E]" />
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mb-2">
            Update Required
          </h2>
          <p className="text-sm text-slate-600 mb-1">
            This version of Ellora ERP is no longer supported.
          </p>
          <p className="text-sm text-slate-500 mb-5">
            Please update to <strong>v{info?.latest_version || 'the latest version'}</strong> to continue.
          </p>
          {info?.release_notes && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-5 text-left text-xs text-slate-600">
              {info.release_notes}
            </div>
          )}
          <button
            onClick={openStore}
            className="w-full bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
            data-testid="update-go-to-store-btn"
          >
            <RefreshCw size={16} />
            Update Now
          </button>
        </div>
      </div>
    );
  }

  // ── Dismissible banner ────────────────────────────────────────────────────
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] px-4 pb-safe-bottom"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
      data-testid="update-banner"
    >
      <div className="max-w-md mx-auto bg-[#1c2a4d] text-white rounded-2xl shadow-2xl px-4 py-3 flex items-center gap-3">
        <ArrowUpCircle size={20} className="text-emerald-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-tight">
            Update available — v{info?.latest_version}
          </p>
          {info?.release_notes && (
            <p className="text-xs text-white/60 mt-0.5 truncate">{info.release_notes}</p>
          )}
        </div>
        <button
          onClick={openStore}
          className="shrink-0 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-display font-bold px-3 py-1.5 rounded-lg transition-colors"
          data-testid="update-banner-update-btn"
        >
          Update
        </button>
        <button
          onClick={dismissBanner}
          className="shrink-0 text-white/40 hover:text-white/80 p-0.5"
          aria-label="Dismiss update notification"
          data-testid="update-banner-dismiss-btn"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
